import { X, Download, Loader2 } from "lucide-react";
import { useState, useRef } from "react";
import { toast } from "react-hot-toast";
import usersService from "../../services/users.service";
import Tooltip from "../../widgets/ToolTip";

interface BulkUploadComponentProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  callBackFunction: () => void;
}

interface CSVRow {
  email: string;
  given_name: string;
  family_name: string;
  mobile_no?: string;
  groups?: string;
}

interface ValidationError {
  row: number;
  column: string;
  value: string;
  error: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  data: CSVRow[];
  totalRows: number;
}

interface BackendUserError {
  status: string;
  message: string;
  email: string;
}

interface BackendResponse {
  invalid_user_list: BackendUserError[];
  validated_user_list: any[];
  skippedCount: number;
  errorCount: number;
  invitedCount: number;
}

const BulkUploadComponent: React.FC<BulkUploadComponentProps> = ({
  isOpen,
  onClose,
  onRefresh,
  callBackFunction
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "success" | "error" | "validating"
  >("idle");
  const [, setBulkUploadId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [backendErrors, setBackendErrors] = useState<BackendUserError[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);



  const requiredHeaders = ["email", "given_name", "family_name", "groups"];
  const headerAliases: Record<string, string> = {
    first_name: "given_name",
    last_name: "family_name",
    mobile: "mobile_no",
    phone: "mobile_no",
  };

    if (!isOpen) return null;

  const isValidEmail = (email: string): boolean =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

const isValidPhone = (phone: string): boolean => {
  const cleaned = phone.trim();

  const pattern = /^\d{10}$/;
  return pattern.test(cleaned);
};


  const parseCSV = (csvContent: string): string[][] => {
  const rows = [];
  let current = [];
  let value = "";
  let insideQuotes = false;

  for (let i = 0; i < csvContent.length; i++) {
    const char = csvContent[i];

    if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === "," && !insideQuotes) {
      current.push(value.trim());
      value = "";
    } else if ((char === "\n" || char === "\r") && !insideQuotes) {
      if (value !== "" || current.length > 0) {
        current.push(value.trim());
        rows.push(current);
        current = [];
        value = "";
      }
    } else {
      value += char;
    }
  }

  if (value !== "" || current.length > 0) {
    current.push(value.trim());
    rows.push(current);
  }

  return rows;
};


const validateCSVContent = async (file: File): Promise<ValidationResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const csvContent = (e.target?.result as string).trim();
        const rows = parseCSV(csvContent);
        const seenEmails = new Set<string>();

        if (rows.length < 2) {
          return resolve({
            isValid: false,
            errors: [{ row: 0, column: "file", value: "", error: "CSV is empty" }],
            data: [],
            totalRows: 0
          });
        }

        const headers = rows[0].map(h => h.toLowerCase());
        const normalized = headers.map(h => headerAliases[h] || h);

        const missingHeaders = requiredHeaders.filter(h => !normalized.includes(h));
        if (missingHeaders.length) {
          return resolve({
            isValid: false,
            errors: missingHeaders.map(h => ({
              row: 0,
              column: h,
              value: "",
              error: `Missing required header: ${h}`
            })),
            data: [],
            totalRows: 0
          });
        }

        const errors: ValidationError[] = [];
        const data: CSVRow[] = [];
        const headerIndex: Record<string, number> = {};
        normalized.forEach((h, i) => headerIndex[h] = i);

        rows.slice(1).forEach((row, index) => {
          const rowNum = index + 2;
          const record: any = {};

          requiredHeaders.forEach((key) => {
            const val = row[headerIndex[key]];
            if (!val) {
              errors.push({
                row: rowNum,
                column: key,
                value: "",
                error: `${key} is required but missing`,
              });
            }
            record[key] = val;
          });

          if (record.email) {
            if (!isValidEmail(record.email)) {
              errors.push({
                row: rowNum,
                column: "email",
                value: record.email,
                error: "Invalid email format"
              });
            } else if (seenEmails.has(record.email.toLowerCase())) {
              errors.push({
                row: rowNum,
                column: "email",
                value: record.email,
                error: "Duplicate email found in CSV"
              });
            } else {
              seenEmails.add(record.email.toLowerCase());
            }
          }

          if (record.groups) {
            const restored = record.groups.replace(/;/g, ",");
            const groupPattern = /^[a-zA-Z0-9_-]+\([a-zA-Z0-9_,-]+\)$/;
            if (!groupPattern.test(restored)) {
              errors.push({
                row: rowNum,
                column: "groups",
                value: record.groups,
                error: "Groups must follow GroupID(role1,role2)"
              });
            }
            record.groups = restored;
          }

          const mobile = row[headerIndex["mobile_no"]];
          if (mobile && !isValidPhone(mobile)) {
            errors.push({
              row: rowNum,
              column: "mobile_no",
              value: mobile,
              error: "Invalid phone number"
            });
          }

          if (!errors.some(e => e.row === rowNum)) {
            data.push(record as CSVRow);
          }
        });

        resolve({
          isValid: errors.length === 0,
          errors,
          data,
          totalRows: rows.length - 1
        });
      } catch {
        resolve({
          isValid: false,
          errors: [{ row: 0, column: "file", value: "", error: "Failed to parse CSV" }],
          data: [],
          totalRows: 0
        });
      }
    };

    reader.readAsText(file);
  });
};



  const handleFileValidation = async (file: File) => {
    if (!file.name.endsWith(".csv")) {
      toast.error("Please select a valid CSV file.");
      return;
    }
    setSelectedFile(file);
    setUploadStatus("validating");
    setValidationResult(null);
    setBackendErrors([]);

    const validation = await validateCSVContent(file);
    setValidationResult(validation);

    if (validation.isValid) {
      setUploadStatus("idle");
      toast.success("CSV validated successfully!");
    } else {
      setUploadStatus("error");
      toast.error(`Validation failed: ${validation.errors.length} issues.`);
    }
  };
  const resetState = () => {
  setSelectedFile(null);
  setValidationResult(null);
  setBackendErrors([]);
  setUploadStatus("idle");
};


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  e.target.value = ""; // ✅ Reset input so same file can trigger change again
  resetState();
  if (file) handleFileValidation(file);
};


  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    resetState();
    if (file) handleFileValidation(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleAreaClick = () => fileInputRef.current?.click();

  const handleDownloadSample = () => {
   const sampleContent = `email,given_name,family_name,groups,mobile_no
john.doe@company.com,John,Doe,"admin(user_admin)",1234567890
jane.smith@company.com,Jane,Smith,"sales(sales_rep)",1234567891
mike.johnson@company.com,Mike,Johnson,"admin(user_admin)",1234567892
sarah.wilson@company.com,Sarah,Wilson,"sales(sales_manager)",1234567893
david.brown@company.com,David,Brown,"admin(content_admin)",1234567894`;

    
    const blob = new Blob([sampleContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_employees.csv";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
    toast.success("Sample CSV downloaded");
  };

  const handleUpload = async () => {
  if (!selectedFile || !validationResult?.isValid) {
    toast.error("Please select and validate a CSV file first.");
    return;
  }

  const text = await selectedFile.text();

  // ✅ Fix: safer line handling with commas inside quotes
    const updatedCSV = text
    .split(/\r?\n/)
    .map((line, index) => {
      if (!line.trim()) return line;
      const parts = line.split(",");

      // Replace semicolons with commas inside parentheses
      const lastPart = parts[parts.length - 1];
      if (lastPart && lastPart.includes("(")) {
        parts[parts.length - 1] = lastPart.replace(/\(([^)]+)\)/, (_, roles) => `(${roles.replace(/;/g, ",")})`);
      }

      // Remove empty mobile_no field (4th column)
      if (index > 0 && parts[3] === "") parts.splice(3, 1);

      return parts.join(",");
    })
    .join("\n");

  const blob = new Blob([updatedCSV], { type: "text/csv" });
  const formData = new FormData();
  formData.append("file", blob, selectedFile.name);

  setUploadStatus("uploading");
  setBackendErrors([]);

  try {
    const response = await usersService.userBulkUpload(formData);
    const data: BackendResponse = response?.data?.user_list;

    if (data?.invalid_user_list?.length) {
      setBackendErrors(data.invalid_user_list);
      setUploadStatus("error");
      callBackFunction();
   
    } else {
      setUploadStatus("success");
      toast.success("Upload successful!");
      onRefresh();
    }

    setSelectedFile(null);
    setValidationResult(null);
    if (response?.data?.id) setBulkUploadId(response.data.id);
  } catch (err) {
    console.error(err);
    setUploadStatus("error");
    toast.error("Upload failed. Please try again.");
  }
};


  const handleClose = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setBackendErrors([]);
    setUploadStatus("idle");
    setIsDragOver(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* HEADER */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10 rounded-t-lg">
          <h2 className="text-xl font-semibold text-gray-900">Bulk Upload Users</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Tooltip content="Close">
              <X className="w-6 h-6" />
            </Tooltip>
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Sample CSV & Upload Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">CSV Format Requirements</h3>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-lg">
                {/* ... existing table content ... */}
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      Field
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      Required
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">email</td>
                    <td className="px-4 py-3 text-sm text-gray-700">string</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                        Required
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">Employee email address (must be unique)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">given_name</td>
                    <td className="px-4 py-3 text-sm text-gray-700">string</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                        Required
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">First name (or use first_name)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">family_name</td>
                    <td className="px-4 py-3 text-sm text-gray-700">string</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                        Required
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">Last name (or use last_name)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">groups</td>
                    <td className="px-4 py-3 text-sm text-gray-700">string</td>
                    <td className="px-4 py-3">
                       <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                        Required
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      Groups and roles: "GroupID(RoleKey1,RoleKey2)"
                    </td>

                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">mobile_no</td>
                    <td className="px-4 py-3 text-sm text-gray-700">string</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                        Optional
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">Mobile phone number (or use mobile/phone)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Groups Format - keep existing */}
<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
  <h4 className="font-medium text-amber-800 mb-2">Groups Format:</h4>
  <div className="space-y-2 text-sm">
    <div>
      <span className="text-amber-700 font-medium">Use format: </span>
      <code className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs">
        "GroupID(RoleKey1,RoleKey2,...)"
      </code>
    </div>
    <div>
      <span className="text-amber-700 font-medium">Example: </span>
      <code className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs">
        "admin(user_admin,content_admin)"
      </code>
    </div>
    <div className="text-gray-700 text-sm">
      Each user can belong to <strong>only one group</strong> but can have <strong>multiple roles</strong> within that group.
    </div>
  </div>
</div>


          {/* Sample CSV Section - keep existing */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sample CSV</h3>
            <button
              onClick={handleDownloadSample}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <Download className="w-4 h-4" /> Download Sample CSV
            </button>
            <p className="text-sm text-gray-600 mt-2">
              Download a sample CSV file with the correct format and example data.
            </p>
          </div>

          {/* Upload Section */}
          <div>
            <h3 className="text-lg font-medium mb-3">Upload CSV</h3>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? "border-blue-400 bg-blue-50"
                  : validationResult?.isValid
                  ? "border-green-400 bg-green-50"
                  : validationResult && !validationResult.isValid
                  ? "border-red-400 bg-red-50"
                  : selectedFile
                  ? "border-yellow-400 bg-yellow-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleAreaClick}
            >
              <div className="space-y-2">
                <div className="mx-auto w-12 h-12 text-gray-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="text-lg font-medium text-gray-900">
                  {uploadStatus === "validating"
                    ? "Validating CSV..."
                    : isDragOver
                    ? "Drop CSV file here"
                    : validationResult?.isValid
                    ? "Valid CSV - click to change"
                    : validationResult && !validationResult.isValid
                    ? "Invalid CSV - click to select another"
                    : selectedFile
                    ? "File selected - click to change"
                    : "Click to select CSV file or drag and drop"}
                </div>
                <div className="text-sm text-gray-500">Only .csv files are accepted</div>
              </div>
            </div>

            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" />

            {/* Validation & User Import Errors */}
            {validationResult && (
              <div className={`mt-3 p-4 border rounded-lg ${validationResult.isValid ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <h4 className={`font-medium ${validationResult.isValid ? "text-green-800" : "text-red-800"}`}>
                  {validationResult.isValid ? "Validation Successful" : "Validation Failed"}
                </h4>
                <p className="text-sm mt-1">
                  {validationResult.isValid
                    ? `${validationResult.data.length} valid rows out of ${validationResult.totalRows}`
                    : `${validationResult.errors.length} CSV errors`}
                </p>

                {!validationResult.isValid && validationResult.errors.length > 0 && (
                  <div className="mt-3 max-h-40 overflow-y-auto">
                    {validationResult.errors.map((err, i) => (
                      <div key={i} className="text-xs bg-red-100 p-2 rounded border-l-4 border-red-400">
                        <strong>Row {err.row === 0 ? "Header" : err.row}, Column "{err.column}":</strong> {err.error}
                        {err.value && <div className="text-red-600">Value: "{err.value}"</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* User Import Errors */}
            {backendErrors.length > 0 && (
              <div className="mt-3 p-4 border rounded-lg bg-red-50 border-red-200 max-h-60 overflow-y-auto">
                <h4 className="font-medium text-red-800">User Import Errors</h4>
                {backendErrors.map((err, i) => (
                  <div key={i} className="text-xs p-2 border-l-4 border-red-400 bg-red-100 rounded mb-1">
                    <strong>{err.email}</strong>: {err.message} ({err.status})
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="p-4 border-t flex justify-end gap-3 sticky bottom-0 bg-white z-10 rounded-b-lg">
          <button onClick={handleClose} disabled={uploadStatus === "uploading" || uploadStatus === "validating"} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700">
            Cancel
          </button>
          <button onClick={handleUpload} disabled={!selectedFile || !validationResult?.isValid || uploadStatus === "uploading"} className={`px-5 py-2 rounded-lg text-white ${!selectedFile || !validationResult?.isValid ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}>
            {uploadStatus === "uploading" || uploadStatus === "validating" ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{uploadStatus === "uploading" ? "Processing..." : "Validating..."}</span>
              </div>
            ) : (
              "Upload"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadComponent;
